import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/first-time-website-owner-guide";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;
const TITLE = "First-Time Website Owner's Guide (2026) — What to Know Before You Buy";
const DESC =
  "Plain-English guide for first-time website owners: domain, hosting, content, SEO, and what each piece actually costs. From a 25-year Colorado agency.";

export const Route = createFileRoute("/learn/first-time-website-owner-guide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: TITLE, description: DESC }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="11 min read"
      title="First-time website owner's guide: what to know before you buy"
      intro="If this is the first website you've ever owned, the vocabulary alone is overwhelming — domain, hosting, CMS, schema, Core Web Vitals. Here's everything a first-time owner actually needs to know, in plain English, with real Colorado price ranges."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "First-time website owner's guide" },
      ]}
      path={PATH}
      faqs={[
        { q: "What's the difference between a domain and hosting?", a: "A domain (yourbusiness.com) is your address — you rent it from a registrar like Namecheap or Google Domains for $12–$20/year. Hosting is the server where your website files live — typically $5–$50/month. They're two separate bills." },
        { q: "Do I need a website if I have a Facebook page or Google Business Profile?", a: "Yes. Social profiles and GBP can disappear, suspend, or change rules overnight. Your website is the only asset you own outright. GBP drives a huge share of local calls but it points back to your site for trust signals and conversion." },
        { q: "How much should a first website cost?", a: "For a Colorado service business: $2,500–$8,000 one-time for a 5–10 page custom site, plus $50/month flat hosting. DIY platforms run $17–$50/month forever and cap your SEO ceiling." },
        { q: "Should I write the content myself?", a: "If you can clearly explain what you do and who you serve, yes — your voice ranks better than generic agency copy. If writing isn't your strength, hire it out. Either way, never use raw AI-generated content as your launch copy." },
        { q: "How long until my website ranks on Google?", a: "Branded searches (your business name) rank in 1–2 weeks. Competitive local searches ('plumber Colorado Springs') take 4–9 months of consistent SEO work. There's no shortcut that survives a Google update." },
      ]}
    >
      <h2>The 6 pieces of a working website</h2>
      <p>Every business website is made of the same 6 things. Buy the wrong version of any one and the whole thing underperforms.</p>

      <h3>1. Domain name ($12–$20/year)</h3>
      <p>Your web address. Buy <strong>yourbusinessname.com</strong> if available, not a .net or .biz. Register it in <strong>your own name</strong> at a real registrar (Namecheap, Cloudflare, Google Domains) — never let a developer or agency own your domain. If they vanish, so does your business.</p>

      <h3>2. Hosting ($5–$50/month)</h3>
      <p>The server your site runs on. For a small Colorado business site, $5–$10/mo is fine on shared hosting (SiteGround, DreamHost). $50/mo gets you fast managed hosting with backups and security included. Avoid GoDaddy's bundle hosting — it's the slowest of the major hosts in independent benchmarks.</p>

      <h3>3. The website itself</h3>
      <p>Three buyer paths:</p>
      <ul>
        <li><strong>DIY platform</strong> (Wix, Squarespace) — $17–$50/mo forever, fast to launch, capped SEO ceiling. <Link to="/learn/custom-web-design-vs-diy-platforms">Full comparison here.</Link></li>
        <li><strong>Freelancer</strong> — $1,500–$5,000 one-time, varies wildly on quality. <Link to="/learn/web-design-vs-freelancer">Freelancer vs agency.</Link></li>
        <li><strong>Custom agency build</strong> — $2,500–$14,000 one-time, you own it, scales with you.</li>
      </ul>

      <h3>4. Content (the writing + images)</h3>
      <p>The single biggest predictor of whether a website converts. Real photos of you and your work beat stock every time. Real customer quotes beat generic testimonials. <Link to="/learn/website-conversion">More on what actually converts.</Link></p>

      <h3>5. Google Business Profile (free)</h3>
      <p>For any local Colorado business, GBP drives more calls than the website itself in the first 12 months. Claim it day one. <Link to="/learn/google-business-profile">GBP setup guide.</Link></p>

      <h3>6. SEO + ongoing maintenance</h3>
      <p>A launched website doesn't rank on its own. Plan on either DIY SEO time (10+ hrs/month) or hire help ($500–$2,500/mo for a Colorado small business). <Link to="/learn/local-seo-guide">Local SEO guide.</Link></p>

      <h2>What first-time owners get wrong</h2>
      <ul>
        <li><strong>Letting the developer register the domain.</strong> Always in your name, on your account, with your credit card.</li>
        <li><strong>Picking a name that's hard to spell or already taken on Google.</strong> Search the .com and the GBP listings <em>before</em> filing the LLC.</li>
        <li><strong>Skipping the GBP because the site isn't done.</strong> GBP can launch the same day. Don't wait.</li>
        <li><strong>Treating launch as the finish line.</strong> Launch is day one of SEO, not the end of the project.</li>
        <li><strong>Buying $5,000 in ads before fixing the website.</strong> Ads to a slow or unclear site set money on fire.</li>
      </ul>

      <h2>The 30-day first website plan</h2>
      <p>Week 1: register domain + LLC + GBP. Week 2: pick build path + start content. Week 3: build pages + photos. Week 4: launch + submit to Google Search Console + first 5 review requests.</p>

      <h2>Related guides for first-time owners</h2>
      <ul>
        <li><Link to="/learn/new-business-website-checklist">New business website checklist (30-day launch plan)</Link></li>
        <li><Link to="/learn/start-a-business-in-colorado">How to start a business in Colorado</Link></li>
        <li><Link to="/learn/web-design-cost-colorado">Web design cost in Colorado</Link></li>
        <li><Link to="/learn/marketing-budget">How to set a marketing budget</Link></li>
        <li><Link to="/learn/custom-web-design-vs-diy-platforms">Custom vs DIY platforms</Link></li>
        <li><Link to="/learn/franchise-location-website-guide">Franchise location website guide</Link></li>
        <li><Link to="/learn/buying-an-existing-business-website">Buying an existing business? Audit the website first</Link></li>
      </ul>
      <h2>Work with us</h2>
      <ul>
        <li><Link to="/web-design">Web design</Link> — custom builds for Colorado businesses</li>
        <li><Link to="/seo">SEO</Link> and <Link to="/local-seo">local SEO</Link> — get found on Google</li>
        <li><Link to="/google-maps">Google Maps optimization</Link></li>
        <li><Link to="/pricing">Transparent pricing</Link></li>
      </ul>

    </ArticleLayout>
  );
}
